import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { createPortal } from "react-dom";

const TableMultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select",
  loading = false,
  noOptionsText = "No options found",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef          = useRef(null);
  const menuRef             = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current    && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (val) => {
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);
  };

  const selectedLabels = options.filter((o) => value.includes(o.value)).map((o) => o.label);

  const rect = triggerRef.current?.getBoundingClientRect();

  return (
    <div className="relative w-full">
      {/* ── Trigger ── */}
      <div
        ref={triggerRef}
        onClick={() => setIsOpen((o) => !o)}
        onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.borderColor = "#FF03F3"; }}
        onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.borderColor = "#d1d5db"; }}
        className={`h-[35px] flex items-center gap-1 px-2 bg-white rounded-md cursor-pointer overflow-hidden select-none transition-colors duration-150 shadow-sm ${
          isOpen
            ? "border-2 border-[#FF03F3] ring-1 ring-[#FF03F3]"
            : "border border-gray-300 hover:border-[#FF03F3]"
        }`}
      >
        {selectedLabels.length === 0 ? (
          <span className="text-[11px] text-gray-400 flex-1">
            {loading ? "Loading..." : placeholder}
          </span>
        ) : selectedLabels.length <= 2 ? (
          <div className="flex gap-1 flex-1 overflow-hidden items-center">
            {selectedLabels.map((label, i) => (
              <span
                key={i}
                className="text-[10px] bg-[rgba(0,0,160,0.08)] text-[#FF03F3] px-[5px] py-[2px] rounded-[3px] whitespace-nowrap max-w-[90px] overflow-hidden text-ellipsis"
              >
                {label}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-[11px] text-[#FF03F3] flex-1">{selectedLabels.length} selected</span>
        )}

        {value.length > 0 && (
          <X
            size={12}
            className="text-gray-500 cursor-pointer shrink-0"
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
          />
        )}

        <ChevronDown
          size={14}
          className={`text-gray-500 shrink-0 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {/* ── Dropdown portal ── */}
      {isOpen &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            className="absolute bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            style={{
              top: rect.bottom + window.scrollY + 2,
              left: rect.left + window.scrollX,
              width: "280px",
              zIndex: 9999999,
            }}
          >
            {/* Search */}
            <div className="px-2 py-[6px] border-b border-gray-200">
              <div className="flex items-center gap-[6px] bg-gray-50 border border-gray-200 rounded-md px-2 py-1">
                <Search size={12} className="text-gray-400 shrink-0" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  className="border-none outline-none bg-transparent text-[11px] w-full text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Options */}
            <div className="max-h-[220px] overflow-y-auto">
              {loading ? (
                <p className="py-3 text-center text-[11px] text-gray-400">Loading...</p>
              ) : filtered.length === 0 ? (
                <p className="py-3 text-center text-[11px] text-gray-400">{noOptionsText}</p>
              ) : (
                filtered.map((opt) => {
                  const checked = value.includes(opt.value);
                  return (
                    <div
                      key={opt.value}
                      onClick={() => toggle(opt.value)}
                      className={`flex items-center gap-2 px-3 py-[7px] cursor-pointer text-[11px] transition-colors duration-100 ${
                        checked
                          ? "bg-[rgba(0,0,160,0.06)] text-[#FF03F3]"
                          : "bg-white text-gray-700 hover:bg-[rgba(0,0,160,0.04)]"
                      }`}
                    >
                      <div
                        className={`w-[16px] h-[16px] shrink-0 rounded-[3px] border-2 flex items-center justify-center transition-all duration-150 ${
                          checked ? "bg-[#FF03F3] border-[#FF03F3]" : "bg-white border-gray-300"
                        }`}
                      >
                        {checked && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path
                              d="M8 2.5L4 7L2 5"
                              stroke="#fff"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="flex-1 break-words">{opt.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TableMultiSelect;
